import { Badge, Button, Select, Tag, Tooltip } from 'antd';
import {
    CalendarOutlined, ContactsOutlined, PlusOutlined, UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, fmtDate, fmtTime, fmtDateApi } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import { PageTitle } from '@extensions/layout';
import { useUrlFilters } from '@extensions/url-filters';
import FilterBar from '@controls/filter-bar/filter-bar';
import CardList, { CardRow } from '@controls/card-list/card-list';
import './task.css';
import Task from './task';
import TaskPriority, { ASSESSMENT_OPTIONS } from './task-priority';
import { versionFilterOptions } from './task-version';
import Contragent from '../contragents/contragent';
import { LogoSlot } from '../contragents/contragent-logo';
import { taskDateGroup } from './task-date-group';
import { blockGroupLabel, duplicateBlockNames, sortTasksByBlock } from './task-block';
import { useWeekPlanIds, WeekPlanStar } from './week-plan';

// vid: 1 — по дате задачи, 2 — по сроку, 3 — незакрытые (по умолчанию), 4 — просроченные
export const VID_OPTIONS = [
    { value: 1, label: 'По дате задачи' },
    { value: 2, label: 'По сроку' },
    { value: 3, label: 'Незакрытые' },
    { value: 4, label: 'Просроченные' },
    { value: 6, label: 'Все' },
];

// Фильтры в URL: ?vid=&p1=&p2=&q=&user=&author=&contragent=&tag=&status=&sort=
const FILTER_SPEC = {
    vid: { param: 'vid', type: 'int', empty: 3 },
    period: { param: 'p', type: 'range', empty: null },
    filter: { param: 'q', type: 'str', empty: '' },
    userId: { param: 'user', type: 'int', empty: 0 },
    authorId: { param: 'author', type: 'int', empty: 0 },
    contragentId: { param: 'contragent', type: 'int', empty: 0 },
    tag: { param: 'tag', type: 'int', empty: 0 },
    statusId: { param: 'status', type: 'int', empty: 0 },
    projectId: { param: 'project', type: 'int', empty: 0 },
    importance: { param: 'importance', type: 'str', empty: null },
    urgency: { param: 'urgency', type: 'str', empty: null },
    version: { param: 'version', type: 'str', empty: null },
    blockId: { param: 'block', type: 'int', empty: 0 },
    sort: { param: 'sort', type: 'str', empty: 'dateDesc' },
    group: { param: 'group', type: 'str', empty: '' },
};

const SORT_OPTIONS = [
    { value: 'dateDesc', label: 'Дата задачи ↓' },
    { value: 'date', label: 'Дата задачи ↑' },
    { value: 'plan', label: 'Срок ↑' },
    { value: 'planDesc', label: 'Срок ↓' },
    { value: 'name', label: 'Наименование ↑' },
    { value: 'nameDesc', label: 'Наименование ↓' },
    { value: 'priority', label: 'Приоритет ↑' },
    { value: 'priorityDesc', label: 'Приоритет ↓' },
    { value: 'user', label: 'Исполнитель ↑' },
    { value: 'userDesc', label: 'Исполнитель ↓' },
    { value: 'status', label: 'Статус ↑' },
    { value: 'statusDesc', label: 'Статус ↓' },
    { value: 'contragent', label: 'Контрагент ↑' },
    { value: 'contragentDesc', label: 'Контрагент ↓' },
    { value: 'project', label: 'Проект ↑' },
    { value: 'projectDesc', label: 'Проект ↓' },
];

const TEXT_FIELDS = {
    name: 'name',
    user: 'userName',
    status: 'statusName',
    contragent: 'contragentName',
    project: 'projectName',
};

const cmpText = (a, b) => {
    const left = (a || '').trim();
    const right = (b || '').trim();
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    return left.localeCompare(right, 'ru', { sensitivity: 'accent' });
};

const cmpIso = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    if (a === b) return 0;
    return a < b ? -1 : 1;
};

// Пустые значения остаются в конце при обоих направлениях. При равенстве — более новый id выше.
const compareTasks = (sort) => (a, b) => {
    const desc = sort.endsWith('Desc');
    const field = desc ? sort.slice(0, -4) : sort;
    const finish = (by, blank) => {
        if (blank) return by || (b.id - a.id);
        if (by !== 0) return desc ? -by : by;
        return b.id - a.id;
    };

    if (TEXT_FIELDS[field]) {
        const key = TEXT_FIELDS[field];
        const by = cmpText(a[key], b[key]);
        return finish(by, !(a[key] || '').trim() || !(b[key] || '').trim());
    }
    if (field === 'date' || field === 'plan') {
        const key = field === 'date' ? 'docDate' : 'planDate';
        let by = cmpIso(a[key], b[key]);
        if (!a[key] || !b[key]) return finish(by, true);
        if (field === 'plan' && by === 0) {
            by = cmpIso(a.planFinish, b.planFinish);
            if (!a.planFinish || !b.planFinish) return finish(by, true);
        }
        return finish(by, false);
    }
    if (field === 'priority') {
        const rank = (item) => [1, 2, 3, 4, 0].indexOf(item.priority ?? 0);
        const aUnset = (a.priority ?? 0) === 0;
        const bUnset = (b.priority ?? 0) === 0;
        if (aUnset !== bUnset) return aUnset ? 1 : -1;
        return finish(rank(a) - rank(b), false);
    }
    return b.id - a.id;
};

const Tasks = () => {
    const { dicts } = useDicts();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilter] = useUrlFilters(FILTER_SPEC, 'taskFilters');
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState({ qtyOpen: 0, qtyToday: 0, qtyOld: 0 });
    const [loading, setLoading] = useState(false);
    const [editTask, setEditTask] = useState(null); // 0 — новая, id — редактирование
    const [editContragent, setEditContragent] = useState(null);
    const weekPlan = useWeekPlanIds();

    // Для режимов «по дате»/«по сроку» без периода — текущий месяц
    const effective = useMemo(() => {
        const vid = filters.vid;
        const needPeriod = vid === 1 || vid === 2;
        const period = filters.period
            ?? (needPeriod ? [fmtDateApi(dayjs().startOf('month')), fmtDateApi(dayjs().endOf('month'))] : null);
        return { vid, period: needPeriod ? period : null };
    }, [filters.vid, filters.period]);

    const load = useCallback(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        qs.append('vid', effective.vid);
        if (effective.period) {
            if (effective.period[0]) qs.append('begPer', effective.period[0]);
            if (effective.period[1]) qs.append('endPer', effective.period[1]);
        }
        if (filters.filter) qs.append('filter', filters.filter);
        if (filters.userId) qs.append('userId', filters.userId);
        if (filters.authorId) qs.append('authorId', filters.authorId);
        if (filters.contragentId) qs.append('contragentId', filters.contragentId);
        if (filters.tag) qs.append('tag', filters.tag);
        if (filters.statusId) qs.append('statusId', filters.statusId);
        if (filters.projectId) qs.append('projectId', filters.projectId);
        if (filters.importance) qs.append('importance', filters.importance);
        if (filters.urgency) qs.append('urgency', filters.urgency);
        if (filters.version) qs.append('version', filters.version);
        if (filters.blockId) qs.append('blockId', filters.blockId);
        serverFetch(`task?${qs.toString()}`)
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, [filters, effective]);

    const loadTotals = useCallback(() => {
        serverFetch(`task/totals?userId=${filters.userId || 0}`)
            .then(setTotals)
            .catch(showError);
    }, [filters.userId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadTotals(); }, [loadTotals]);

    // Открыть карточку по ?id=
    useEffect(() => {
        const id = Number(searchParams.get('id'));
        if (id > 0) setEditTask(id);
    }, [searchParams]);

    const closeTask = () => {
        setEditTask(null);
        if (searchParams.get('id')) {
            searchParams.delete('id');
            setSearchParams(searchParams, { replace: true });
        }
        load();
        loadTotals();
        weekPlan.reload();
    };

    const onDelete = (record) => {
        serverFetch(`task/${record.id}`, { method: 'DELETE' })
            .then(() => { load(); loadTotals(); })
            .catch(showError);
    };

    const userOptions = useMemo(() => dicts.users.map((u) => ({ value: u.id, label: u.name })), [dicts.users]);

    const [contragentOptions, setContragentOptions] = useState([]);

    useEffect(() => {
        const id = filters.contragentId;
        if (id && !contragentOptions.some((o) => o.value === id)) {
            serverFetch(`contragent/${id}`)
                .then((c) => setContragentOptions((prev) => (
                    prev.some((o) => o.value === c.id) ? prev : [...prev, { value: c.id, label: c.name }]
                )))
                .catch(showError);
        }
    }, [filters.contragentId, contragentOptions]);

    const searchContragents = useCallback((text) => (
        !text || text.length < 2
            ? Promise.resolve([])
            : serverFetch(`contragent/search?name=${encodeURIComponent(text)}&limit=20`)
                .then((list) => {
                    const opts = list.map((c) => ({ value: c.id, label: c.name }));
                    setContragentOptions((prev) => {
                        const known = new Set(prev.map((o) => o.value));
                        return [...prev, ...opts.filter((o) => !known.has(o.value))];
                    });
                    return opts;
                })
    ), []);

    const filterDefs = useMemo(() => [
        { key: 'vid', label: 'Режим', emptyValue: 3, options: VID_OPTIONS },
        { key: 'period', label: 'Период', type: 'range', emptyValue: null },
        {
            key: 'userId', label: 'Исполнитель', emptyValue: 0,
            options: [{ value: -1, label: 'Без исполнителя' }, ...userOptions],
        },
        { key: 'authorId', label: 'Автор', emptyValue: 0, options: userOptions },
        {
            key: 'contragentId', label: 'Контрагент', type: 'search', emptyValue: 0,
            options: contragentOptions, search: searchContragents,
            placeholder: 'Имя, телефон, email, адрес…',
        },
        {
            key: 'statusId', label: 'Статус', emptyValue: 0,
            options: [{ value: -1, label: 'Без статуса' }, ...dicts.statuses.map((s) => ({ value: s.id, label: s.name }))],
        },
        { key: 'importance', label: 'Важность', emptyValue: null, options: ASSESSMENT_OPTIONS },
        { key: 'urgency', label: 'Срочность', emptyValue: null, options: ASSESSMENT_OPTIONS },
        {
            key: 'tag', label: 'Метка', emptyValue: 0,
            options: dicts.tags.filter((t) => t.vid === 4).map((t) => ({ value: t.id, label: t.name })),
        },
        {
            key: 'projectId', label: 'Проект', emptyValue: 0,
            options: [{ value: -1, label: 'Без проекта' }, ...dicts.projects.map((p) => ({ value: p.id, label: p.name }))],
        },
        { key: 'version', label: 'Версия', emptyValue: null, options: versionFilterOptions(dicts.versions) },
        {
            key: 'blockId', label: 'Блок', emptyValue: 0,
            options: [
                { value: -1, label: 'Без блока' },
                ...[...(dicts.blocks ?? [])]
                    .filter((b) => filters.projectId <= 0 || b.projectId === filters.projectId)
                    .sort((a, b) => (a.projectName || '').localeCompare(b.projectName || '', 'ru')
                        || (a.idx ?? 0) - (b.idx ?? 0)
                        || (a.name || '').localeCompare(b.name || '', 'ru'))
                    .map((b) => ({
                        value: b.id,
                        label: filters.projectId > 0 ? b.name : `${b.projectName}: ${b.name}`,
                    })),
            ],
        },
    ], [dicts.statuses, dicts.tags, dicts.projects, dicts.versions, dicts.blocks, filters.projectId, userOptions, contragentOptions, searchContragents]);

    const sort = SORT_OPTIONS.some((o) => o.value === filters.sort) ? filters.sort : 'dateDesc';
    const groupByBlock = filters.group === 'block';
    const rows = useMemo(() => {
        const sorted = [...items].sort(compareTasks(sort));
        return groupByBlock ? sortTasksByBlock(sorted, compareTasks(sort)) : sorted;
    }, [items, sort, groupByBlock]);
    const dateGroupField = sort === 'dateDesc' ? 'docDate' : sort === 'planDesc' ? 'planDate' : null;
    const blockNameDups = useMemo(() => duplicateBlockNames(rows), [rows]);
    const groupOptions = [
        { value: '', label: dateGroupField ? 'По дате' : 'Без группировки' },
        { value: 'block', label: 'По блоку' },
    ];

    const tagById = useMemo(() => new Map(dicts.tags.map((t) => [t.id, t])), [dicts.tags]);
    const projectById = useMemo(() => new Map(dicts.projects.map((p) => [p.id, p])), [dicts.projects]);
    const statusById = useMemo(() => new Map(dicts.statuses.map((s) => [s.id, s])), [dicts.statuses]);

    const today = dayjs().startOf('day');
    const isOverdue = (r) => !r.isClosed && r.planDate && dayjs(r.planDate).isBefore(today);

    return (
        <div>
            <div className="page-toolbar">
                <PageTitle>Задачи</PageTitle>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditTask(0)}>Добавить</Button>
                <FilterBar
                    filters={filterDefs}
                    value={filters}
                    onChange={setFilter}
                    searchValue={filters.filter}
                    onSearch={(v) => setFilter({ filter: v })}
                    placeholder="Поиск задач"
                />
                <Select
                    className="task-sort"
                    value={sort}
                    onChange={(value) => setFilter({ sort: value })}
                    options={SORT_OPTIONS}
                    popupMatchSelectWidth={false}
                    title="Сортировка"
                />
                <Select
                    className="task-group"
                    value={groupByBlock ? 'block' : ''}
                    onChange={(value) => setFilter({ group: value })}
                    options={groupOptions}
                    popupMatchSelectWidth={false}
                    title="Группировка"
                />
                <span className="page-toolbar-info" style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
                    {[
                        { count: items.length, label: 'Задач в текущем списке', color: '#1677ff' },
                        { count: totals.qtyOpen, label: 'Всего открытых задач', color: '#52c41a' },
                        { count: totals.qtyToday, label: 'Задачи на сегодня', color: '#fa8c16' },
                        { count: totals.qtyOld, label: 'Просроченные задачи', color: '#ff4d4f' },
                    ].map(({ count, label, color }) => (
                        <Tooltip key={label} title={`${label}: ${count}`}>
                            <span tabIndex={0} aria-label={`${label}: ${count}`}>
                                <Badge count={count} showZero overflowCount={Infinity} color={color} />
                            </span>
                        </Tooltip>
                    ))}
                </span>
            </div>
            <CardList
                items={rows}
                loading={loading}
                pageSize={30}
                emptyText="Задач нет"
                deleteTitle="Удалить задачу?"
                onClick={(r) => setEditTask(r.id)}
                onDelete={onDelete}
                rowStyle={(r) => (r.isClosed ? { opacity: 0.6 } : undefined)}
                renderAside={(r) => (
                    <LogoSlot fileId={r.contragentLogoFileId} name={r.contragentLogoName || r.contragentName} empty />
                )}
                groupOf={groupByBlock
                    ? (r) => blockGroupLabel(r, blockNameDups)
                    : (dateGroupField ? (r) => taskDateGroup(r[dateGroupField]) : undefined)}
                renderTitle={(r) => {
                    const st = statusById.get(r.statusId);
                    return (
                        <>
                            <WeekPlanStar active={weekPlan.ids.has(r.id)} onClick={() => weekPlan.toggle(r.id)} />
                            <TaskPriority priority={r.priority} />
                            <span>{r.name}</span>
                            <span className="card-list-muted">#{r.id}</span>
                            {r.statusName && <Tag color={st?.color || undefined}>{r.statusName}</Tag>}
                            {r.projectId && (
                                <Tag color={projectById.get(r.projectId)?.color || undefined}>
                                    {r.projectName || `#${r.projectId}`}
                                </Tag>
                            )}
                            {r.blockName && <Tag>{r.blockName}</Tag>}
                            {r.versionName && <Tag>v{r.versionName}</Tag>}
                            {(r.tags ?? []).map((id) => {
                                const t = tagById.get(id);
                                return t ? (
                                    <Tag key={id} color={t.bgColor || undefined} style={{ color: t.textColor || undefined }}>{t.name}</Tag>
                                ) : null;
                            })}
                        </>
                    );
                }}
                renderBody={(r) => (
                    <>
                        <CardRow icon={<CalendarOutlined />}>{fmtDate(r.docDate)}</CardRow>
                        {r.planDate && (
                            <CardRow title="Срок">
                                <span style={isOverdue(r) ? { color: 'red' } : undefined}>
                                    срок {fmtDate(r.planDate)}{r.planFinish ? ` ${fmtTime(r.planFinish)}` : ''}
                                </span>
                            </CardRow>
                        )}
                        <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>
                        {r.contragentId && (
                            <CardRow icon={<ContactsOutlined />}>
                                <Button
                                    type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                    onClick={(e) => { e.stopPropagation(); setEditContragent(r.contragentId); }}
                                >
                                    {r.contragentName || `#${r.contragentId}`}
                                </Button>
                            </CardRow>
                        )}
                        <CardRow className="card-list-muted card-list-wide">{r.comment}</CardRow>
                    </>
                )}
            />
            {editTask !== null && (
                <Task
                    id={editTask}
                    onClose={closeTask}
                    onOpenContragent={(id) => setEditContragent(id)}
                />
            )}
            {editContragent !== null && (
                <Contragent id={editContragent} onClose={() => setEditContragent(null)} />
            )}
        </div>
    );
};

export default Tasks;
